import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, StringDecoder } from '@common/encoding';
import { v4 as uuidv4 } from 'uuid';
import { Language } from '@stamhoofd/types/Language';
import { Address } from './addresses/Address.js';
import { appToUri, getAppHost } from './AppType.js';
import { Company } from './Company.js';
import { compileToInMemoryFilter } from './filters/InMemoryFilter.js';
import { organizationItemInMemoryFilterCompilers } from './filters/inMemoryFilterDefinitions.js';
import { Group } from './Group.js';
import { OrganizationMetaData } from './OrganizationMetaData.js';
import { OrganizationPrivateMetaData } from './OrganizationPrivateMetaData.js';
import { OrganizationType } from './OrganizationType.js';
import { OrganizationRegistrationPeriod, RegistrationPeriod } from './RegistrationPeriod.js';
import { UmbrellaOrganization } from './UmbrellaOrganization.js';
import { Webshop, WebshopPreview } from './webshops/Webshop.js';
export class BaseOrganization extends AutoEncoder {
    id;
    /**
     * Name of the organization you are creating
     */
    name = '';
    active = true;
    website = null;
    registerDomain = null;
    uri = '';
    meta;
    address = Address.create({});
    createdAt = new Date();
    /**
     * Null falls back to the language of the platform.
     */
    language = null;
    /**
     * Return default locale confiruation
     */
    get i18n() {
        return { language: Language.Dutch, locale: 'nl-' + this.address.country };
    }
    /**
     * Potentially includes a path
     */
    getRegistrationHost(i18n) {
        return getAppHost('registration', this, false, i18n);
    }
    /**
     * Used for redirecting
     */
    get resolvedRegisterDomain() {
        if (this.registerDomain) {
            return this.registerDomain;
        }
        if (!STAMHOOFD.domains.registration) {
            return null;
        }
        return this.uri + '.' + (STAMHOOFD.domains.registration[this.address.country] ?? STAMHOOFD.domains.registration['']);
    }
    get registerUrl() {
        return 'https://' + this.getRegistrationHost(false);
    }
    get dashboardUrl() {
        return 'https://' + STAMHOOFD.domains.dashboard + '/' + appToUri('dashboard') + '/' + this.uri;
    }
    get dashboardDomain() {
        return STAMHOOFD.domains.dashboard;
    }
    /**
     * Assures at least one company at all times
     */
    get defaultCompanies() {
        return this.meta.companies.length
            ? this.meta.companies
            : [
                Company.create({
                    name: this.name,
                    address: this.address,
                }),
            ];
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], BaseOrganization.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], BaseOrganization.prototype, "name", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 325 })
], BaseOrganization.prototype, "active", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], BaseOrganization.prototype, "website", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], BaseOrganization.prototype, "registerDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 3, upgrade: () => '' })
], BaseOrganization.prototype, "uri", void 0);
__decorate([
    field({ decoder: OrganizationMetaData, defaultValue: () => OrganizationMetaData.create({}) })
], BaseOrganization.prototype, "meta", void 0);
__decorate([
    field({ decoder: Address })
], BaseOrganization.prototype, "address", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 259 })
], BaseOrganization.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: new EnumDecoder(Language), nullable: true, version: 414 })
], BaseOrganization.prototype, "language", void 0);
export class Organization extends BaseOrganization {
    hasFutureEvents = true;
    /**
     * @deprecated
     * Please use period instead now
     */
    groups = [];
    period;
    /**
     * Get all groups that are in a category
     */
    get availableGroups() {
        return this.period.categoryTree.getAllGroups();
    }
    getGroupsForPermissions(permissions) {
        return this.getCategoryTree({ permissions }).getAllGroups();
    }
    get adminAvailableGroups() {
        return this.period.adminCategoryTree.getAllGroups();
    }
    /**
     * @deprecated
     *
     * Get all groups that are in a category
     */
    get availableCategories() {
        return this.period.availableCategories;
    }
    /**
     * @deprecated
     */
    get categoryTree() {
        return this.period.categoryTree;
    }
    /**
     * @deprecated
     */
    get adminCategoryTree() {
        return this.getCategoryTree({ admin: true });
    }
    isAcceptingNewMembers() {
        return !this.meta.blockCreatingNewMembers;
    }
    isAcceptingExistingMembers(admin) {
        const groups = this.getCategoryTree({ admin }).getAllGroups();
        for (const group of groups) {
            if (group.closed) {
                continue;
            }
            if (group.settings.availableMembers === 0 && !group.waitingList) {
                continue;
            }
            return true;
        }
        return false;
    }
    /**
     * Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     *
     * For registration members perspective, try to use options.admin instead of options.permissions.
     * options.permissions is only used if you want to hide groups and empty categories that you don't have permissions for.
     */
    getCategoryTree(options) {
        return this.period.getCategoryTree(options ? { ...options } : {});
    }
    /**
     * @deprecated
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    getCategoryTreeWithDepth(maxDepth) {
        return this.getCategoryTree({ maxDepth });
    }
    /**
     * Only set for users with full access to the organization
     */
    privateMeta = null;
    /**
     * Only set for users with full access to the organization
     */
    webshops = [];
    /**
     * @deprecated
     * This way of caching is discouraged and unstable because it gets overriden easily using deepSet
     */
    periods;
    /**
     * Whether internal administrators (members with responsibilities/functions) are relevant for this organization.
     * In platform mode this is always the case. In organization mode it only makes sense when the members package is used.
     */
    get showInternalAdmins() {
        return (this.meta.packages.useMembers && this.privateMeta && (this.privateMeta.responsibilities.length > 0)) || STAMHOOFD.userMode === 'platform';
    }
    get canShowInternalAdmins() {
        return this.meta.packages.useMembers || STAMHOOFD.userMode === 'platform';
    }
    isRecordEnabled(_record) {
        return true;
    }
    getRecordAnswers() {
        return this.privateMeta?.recordAnswers ?? new Map();
    }
    patchRecordAnswers(patch) {
        return this.patch({
            privateMeta: OrganizationPrivateMetaData.patch({
                recordAnswers: patch,
            }),
        });
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, organizationItemInMemoryFilterCompilers);
            const value = compiledFilter(this);
            console.log('does match filter', filter, this, value);
            return value;
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    updatePeriods(periods) {
        // Update in memory
        for (const period of this.periods?.organizationPeriods ?? []) {
            const updated = periods.find(p => p.id === period.id);
            if (updated) {
                period.deepSet(updated);
            }
        }
        const updated = periods.find(p => p.id === this.period.id);
        if (updated) {
            this.period.deepSet(updated);
        }
        // Add missing periods
    }
    /** Returns true when the organization is managed by Scouts en Gidsen Vlaanderen. */
    get isSGVSyncOrganization() {
        return this.meta.type === OrganizationType.Youth
            && this.meta.umbrellaOrganization === UmbrellaOrganization.ScoutsEnGidsenVlaanderen;
    }
}
__decorate([
    field({ decoder: BooleanDecoder, version: 400 })
], Organization.prototype, "hasFutureEvents", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), version: 2, upgrade: () => [], optional: true })
], Organization.prototype, "groups", void 0);
__decorate([
    field({ decoder: OrganizationRegistrationPeriod, version: 264, defaultValue: () => OrganizationRegistrationPeriod.create({ period: RegistrationPeriod.create({}) }) })
], Organization.prototype, "period", void 0);
__decorate([
    field({ decoder: OrganizationPrivateMetaData, nullable: true, version: 6 })
], Organization.prototype, "privateMeta", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 38, upgrade: () => [] })
], Organization.prototype, "webshops", void 0);
export class GetWebshopFromDomainResult extends AutoEncoder {
    organization;
    /**
     * There is one specific webshop for the given domain.
     */
    webshop = null;
    /**
     * Multiple webshops possible. Show a selection page.
     */
    webshops = [];
}
__decorate([
    field({ decoder: Organization })
], GetWebshopFromDomainResult.prototype, "organization", void 0);
__decorate([
    field({ decoder: Webshop, nullable: true })
], GetWebshopFromDomainResult.prototype, "webshop", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 137 })
], GetWebshopFromDomainResult.prototype, "webshops", void 0);
//# sourceMappingURL=Organization.js.map